'use client'

import { useEffect, useRef, useState } from 'react'
import { FishingEventType, type FishingSaleEvent } from '@/lib/fishing-events'

const MIN_SOUND_GAP_MS = 800
const CATCH_OUTPUT_GAIN = 33.12
const CATCH_MAKEUP_GAIN = 1.45
export type CatchSoundVariant = 0 | 1 | 2 | 3 | 4

const SOUND_PROFILES: ReadonlyArray<{ plop: number; notes: readonly [number, number] }> = [
  { plop: 165, notes: [392, 523.25] },
  { plop: 148, notes: [349.23, 493.88] },
  { plop: 176, notes: [440, 587.33] },
  { plop: 138, notes: [329.63, 440] },
  { plop: 188, notes: [493.88, 659.25] },
]
const RELEASE_SOUND_PROFILES: ReadonlyArray<{ start: number; end: number; drop: number }> = [
  { start: 587.33, end: 293.66, drop: 118 },
  { start: 523.25, end: 261.63, drop: 105 },
  { start: 659.25, end: 329.63, drop: 132 },
  { start: 493.88, end: 246.94, drop: 98 },
  { start: 698.46, end: 349.23, drop: 142 },
]

type BrowserAudioContext = typeof AudioContext

function audioContextConstructor(): BrowserAudioContext | null {
  if (typeof window === 'undefined') return null
  return window.AudioContext ?? null
}

function eventVariant(id: string): CatchSoundVariant {
  let hash = 0
  for (let index = 0; index < id.length; index += 1) hash = ((hash * 31) + id.charCodeAt(index)) >>> 0
  return (hash % SOUND_PROFILES.length) as CatchSoundVariant
}

function primeAudioContext(context: AudioContext) {
  const now = context.currentTime
  const oscillator = context.createOscillator()
  const silentGain = context.createGain()
  silentGain.gain.setValueAtTime(0.0001, now)
  oscillator.connect(silentGain).connect(context.destination)
  oscillator.start(now)
  oscillator.stop(now + 0.01)
}

function scheduleSoftCatch(context: AudioContext, variant: CatchSoundVariant) {
  const now = context.currentTime
  const profile = SOUND_PROFILES[variant]
  const output = context.createGain()
  const limiter = context.createDynamicsCompressor()
  const makeup = context.createGain()
  output.gain.setValueAtTime(CATCH_OUTPUT_GAIN, now)
  makeup.gain.setValueAtTime(CATCH_MAKEUP_GAIN, now)
  limiter.threshold.setValueAtTime(-5, now)
  limiter.knee.setValueAtTime(8, now)
  limiter.ratio.setValueAtTime(10, now)
  limiter.attack.setValueAtTime(0.003, now)
  limiter.release.setValueAtTime(0.18, now)
  output.connect(limiter).connect(makeup).connect(context.destination)

  const plop = context.createOscillator()
  const plopGain = context.createGain()
  plop.type = 'sine'
  plop.frequency.setValueAtTime(profile.plop, now)
  plop.frequency.exponentialRampToValueAtTime(78, now + 0.22)
  plopGain.gain.setValueAtTime(0.0001, now)
  plopGain.gain.exponentialRampToValueAtTime(0.095, now + 0.018)
  plopGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25)
  plop.connect(plopGain).connect(output)
  plop.start(now)
  plop.stop(now + 0.27)

  profile.notes.forEach((frequency, index) => {
    const start = now + 0.11 + index * 0.1
    const tone = context.createOscillator()
    const toneGain = context.createGain()
    tone.type = 'sine'
    tone.frequency.setValueAtTime(frequency, start)
    toneGain.gain.setValueAtTime(0.0001, start)
    toneGain.gain.exponentialRampToValueAtTime(0.072, start + 0.035)
    toneGain.gain.exponentialRampToValueAtTime(0.0001, start + 0.58)
    tone.connect(toneGain).connect(output)
    tone.start(start)
    tone.stop(start + 0.62)
  })
}

function scheduleSoftRelease(context: AudioContext, variant: CatchSoundVariant) {
  const now = context.currentTime
  const profile = RELEASE_SOUND_PROFILES[variant]
  const output = context.createGain()
  const limiter = context.createDynamicsCompressor()
  output.gain.setValueAtTime(24.84, now)
  limiter.threshold.setValueAtTime(-5, now)
  limiter.knee.setValueAtTime(8, now)
  limiter.ratio.setValueAtTime(10, now)
  limiter.attack.setValueAtTime(0.003, now)
  limiter.release.setValueAtTime(0.2, now)
  output.connect(limiter).connect(context.destination)

  const glide = context.createOscillator()
  const glideGain = context.createGain()
  glide.type = 'sine'
  glide.frequency.setValueAtTime(profile.start, now)
  glide.frequency.exponentialRampToValueAtTime(profile.end, now + 0.48)
  glideGain.gain.setValueAtTime(0.0001, now)
  glideGain.gain.exponentialRampToValueAtTime(0.055, now + 0.025)
  glideGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.55)
  glide.connect(glideGain).connect(output)
  glide.start(now)
  glide.stop(now + 0.58)

  const drop = context.createOscillator()
  const dropGain = context.createGain()
  const dropAt = now + 0.34
  drop.type = 'sine'
  drop.frequency.setValueAtTime(profile.drop, dropAt)
  drop.frequency.exponentialRampToValueAtTime(62, dropAt + 0.24)
  dropGain.gain.setValueAtTime(0.0001, dropAt)
  dropGain.gain.exponentialRampToValueAtTime(0.085, dropAt + 0.018)
  dropGain.gain.exponentialRampToValueAtTime(0.0001, dropAt + 0.3)
  drop.connect(dropGain).connect(output)
  drop.start(dropAt)
  drop.stop(dropAt + 0.32)
}

export function useFishingCatchSound(activeEvent: FishingSaleEvent | null) {
  const [audioReady, setAudioReady] = useState(false)
  const contextRef = useRef<AudioContext | null>(null)
  const unlockedRef = useRef(false)
  const lastEventIdRef = useRef<string | null>(null)
  const pendingEventRef = useRef<FishingSaleEvent | null>(null)
  const lastPlayedAtRef = useRef<number | null>(null)

  const play = (
    type: FishingEventType,
    variant: CatchSoundVariant,
    rateLimited = true,
    onStarted?: () => void,
    shouldStart?: () => boolean,
  ) => {
    const AudioContextConstructor = audioContextConstructor()
    if (!AudioContextConstructor) return
    const context = contextRef.current ?? new AudioContextConstructor()
    contextRef.current = context
    const start = () => {
      if (context.state !== 'running') return
      if (shouldStart && !shouldStart()) return
      unlockedRef.current = true
      setAudioReady(true)
      const now = performance.now()
      if (rateLimited && lastPlayedAtRef.current !== null && now - lastPlayedAtRef.current < MIN_SOUND_GAP_MS) return
      lastPlayedAtRef.current = now
      if (type === FishingEventType.Release) scheduleSoftRelease(context, variant)
      else scheduleSoftCatch(context, variant)
      onStarted?.()
    }
    if (context.state === 'running') start()
    else void context.resume().then(start).catch(() => undefined)
  }

  const enableAudio = () => {
    const AudioContextConstructor = audioContextConstructor()
    if (!AudioContextConstructor) return
    const context = contextRef.current ?? new AudioContextConstructor()
    contextRef.current = context
    const finishUnlock = () => {
      if (context.state !== 'running') return
      unlockedRef.current = true
      setAudioReady(true)
      const pendingEvent = pendingEventRef.current
      if (!pendingEvent || lastEventIdRef.current === pendingEvent.id) return
      play(pendingEvent.type, eventVariant(pendingEvent.id), true, () => {
        lastEventIdRef.current = pendingEvent.id
        if (pendingEventRef.current?.id === pendingEvent.id) pendingEventRef.current = null
      }, () => pendingEventRef.current?.id === pendingEvent.id)
    }
    if (context.state === 'running') finishUnlock()
    else {
      primeAudioContext(context)
      void context.resume().then(finishUnlock).catch(() => undefined)
    }
  }

  useEffect(() => {
    document.addEventListener('pointerdown', enableAudio, { passive: true })
    document.addEventListener('keydown', enableAudio)
    return () => {
      document.removeEventListener('pointerdown', enableAudio)
      document.removeEventListener('keydown', enableAudio)
    }
  }, [])

  useEffect(() => {
    if (!activeEvent) {
      pendingEventRef.current = null
      return
    }
    if (lastEventIdRef.current === activeEvent.id) return
    pendingEventRef.current = activeEvent
    play(activeEvent.type, eventVariant(activeEvent.id), true, () => {
      lastEventIdRef.current = activeEvent.id
      if (pendingEventRef.current?.id === activeEvent.id) pendingEventRef.current = null
    }, () => pendingEventRef.current?.id === activeEvent.id)
  }, [activeEvent])

  useEffect(() => () => {
    const context = contextRef.current
    if (context) void context.close().catch(() => undefined)
  }, [])

  return {
    audioReady,
    enableAudio,
    playPreview: (type: FishingEventType, variant: CatchSoundVariant) => play(type, variant, false),
  }
}
